import { CSSProperties, ReactElement, RefObject, useEffect, useRef, useState } from 'react';
import { classes, style } from 'typestyle';
import { LocalizedStrings } from '@src/utils/localizedStrings';
import {
  SuggestionsDef, SearchTheme, SearchMarkDef, SearchEventHandlers, SEARCH_MARK_TYPES, SearchMarkTypes, SearchElementRef,
} from '@src/types/Search';
import { Mark } from '@src/components/Mark';
import { PerfectScrollbar } from '@src/components/PerfectScrollbar';
import { LinearLoader } from '@src/components/LinearLoader';
import { markEqual } from './utils/markEqual';

interface Props {
  suggestions: SuggestionsDef
  theme: SearchTheme
  handlers: SearchEventHandlers
  showLoader: boolean
  hightlightedSuggestion?: SearchMarkDef | null
  exclusion: boolean
  elementRefs: SearchElementRef
  compact?: boolean
  wide?: boolean
  anchorRef?: RefObject<HTMLElement | null>
}

const EXCLUSION_SUGGEST_COLOR = '#9e4e4e';

export function Suggestions({
  suggestions,
  theme,
  handlers,
  showLoader,
  hightlightedSuggestion,
  exclusion,
  elementRefs,
  compact = false,
  wide = false,
  anchorRef,
}: Props) {
  const highlightedRef = useRef<HTMLDivElement | null>(null);
  const lastScrollRef = useRef<number>(0);
  const lastAutomaticScrollRef = useRef<number>(0);
  const [fixedStyle, setFixedStyle] = useState<CSSProperties>();

  // Reset on each render; will be set by a ref callback if a highlighted item is present
  highlightedRef.current = null;

  useEffect(() => {
    ensureHightlightedIsVisible();
  });

  useEffect(() => {
    if (!wide) {
      setFixedStyle(undefined);
      return undefined;
    }

    const updatePosition = () => {
      const anchor = anchorRef?.current ?? elementRefs.bar.current;
      if (!anchor) {
        return;
      }

      const rect = anchor.getBoundingClientRect();
      const viewportPadding = 30;
      const availableWidth = window.innerWidth - rect.left - viewportPadding;
      setFixedStyle({
        position: 'fixed',
        top: rect.bottom + 13,
        left: rect.left,
        width: Math.min(600, Math.max(rect.width, availableWidth)),
        maxWidth: `calc(100vw - ${rect.left + viewportPadding}px)`,
        marginTop: 0,
      });
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [anchorRef, elementRefs.bar, wide]);

  function ensureHightlightedIsVisible() {
    const elementPos = highlightedRef.current?.offsetTop;
    const elementSize = highlightedRef.current?.offsetHeight;
    const container = highlightedRef.current?.parentElement?.parentElement;
    const scrollTop = container?.scrollTop;
    const containerSize = container?.offsetHeight;

    if (Date.now() - lastScrollRef.current < 1000 || typeof elementPos !== 'number'
            || typeof elementSize !== 'number'
            || typeof scrollTop !== 'number'
            || typeof containerSize !== 'number'
    ) {
      return;
    }
    const hiddenBefore = elementPos - scrollTop < 0;
    const hiddenAfter = (elementPos + elementSize) - (scrollTop + containerSize) > 0;

    if (hiddenBefore) {
      container?.scrollTo({ top: elementPos });
    } else if (hiddenAfter) {
      container?.scrollTo({ top: elementPos - (containerSize - elementSize) });
    } else {
      return;
    }
    lastAutomaticScrollRef.current = Date.now();
  }

  function onScroll() {
    if (Date.now() - lastAutomaticScrollRef.current < 500) {
      return;
    }
    lastScrollRef.current = Date.now();
  }

  function renderContainerMark(markDef: SearchMarkDef, mark: ReactElement) {
    const isHighlighted = hightlightedSuggestion ? markEqual(markDef, hightlightedSuggestion) : false;
    return (
      <div
        className={markContainerClass}
        key={mark.key?.toString()}
        data-hightlight={isHighlighted}
        ref={(ref) => { if (isHighlighted) highlightedRef.current = ref; }}
        onMouseOver={() => handlers.onSuggestionOver(markDef)}
      >
        {mark}
      </div>
    );
  }

  function renderCategory(title: string, entries: SearchMarkDef[], typedText?: string) {
    const markClasses = [markClass];
    if (exclusion) {
      markClasses.push(exclusionClass);
    }
    return (
      <div className={classes(categoryClass, compact ? categoryCompactClass : undefined)} key={title}>
        <div className={suggestionTitleClass}>{title}</div>
        <PerfectScrollbar className={entriesContainerClass} onScroll={onScroll}>
          <div>
            {entries.map((e) => (
              renderContainerMark(e,
                <Mark
                  key={e.text}
                  classes={markClasses}
                  markDef={e}
                  highlight={typedText}
                  onClick={handlers.onClickSuggestion.bind(handlers)}
                  suggestColor={exclusion ? EXCLUSION_SUGGEST_COLOR : undefined}
                />)
            ))}
            {entries.length === 0 ? (
            <div className={categoryEmptyClass}>{lang.formatString(lang.emptyCategory, { type: title })}</div>
            ) : null}
          </div>
        </PerfectScrollbar>
      </div>
    );
  }

  return (
    <div
      className={classes(
        suggestionsClass,
        compact ? suggestionsCompactClass : undefined,
        wide ? suggestionsWideClass : undefined
      )}
      style={fixedStyle}
      tabIndex={0}
      ref={elementRefs.suggestions}
    >
      <LinearLoader
        height={3}
        display={showLoader}
        classes={[loaderClass]}
      />
      {SEARCH_MARK_TYPES.map((k) => {
        const { title } = theme[k];
        return renderCategory(title, suggestions[k], suggestions.text[0]?.text);
      })}
    </div>
  );
}

const lang = LocalizedStrings({
  en: {
    emptyCategory: 'No {type}',
  },
});

const suggestionsClass = style({
  position: 'absolute',
  display: 'flex',
  flexDirection: 'row',
  overflow: 'hidden',
  width: '100%',
  maxWidth: 600,
  padding: '15px 12px',
  backgroundColor: '#23202a',
  borderRadius: 7,
  border: '1px solid transparent',
  top: '100%',
  marginTop: 13,
  zIndex: 2,
  fontSize: 12,
  color: '#e0e0e0',
  outline: 'none',
  $nest: {
    '&:focus': {
      borderColor: '#1c3656',
    },
  },
});

const suggestionsCompactClass = style({
  flexDirection: 'column',
  maxWidth: 'none',
  marginTop: 8,
  padding: '10px',
  gap: 8,
});

const suggestionsWideClass = style({
  width: 'calc(100vw - 60px)',
  maxWidth: 600,
});

const categoryClass = style({
  flex: 1,
  overflow: 'hidden',
  padding: '0 4px 0 0',
  position: 'relative',
});

const categoryCompactClass = style({
  width: '100%',
  padding: 0,
});

const entriesContainerClass = style({
  maxHeight: 98,
  overflow: 'hidden',
  position: 'relative',
});

const categoryEmptyClass = style({
  color: '#464646',
  paddingLeft: 4,
});

const suggestionTitleClass = style({
  fontWeight: 'bold',
  color: 'white',
});
const markContainerClass = style({
  cursor: 'pointer',
  $nest: {
    '&[data-hightlight="true"]': {
      $nest: {
        '& *': {
          backgroundColor: '#0e199c',
        },
      },
    },
  },
});

const markClass = style({
  paddingLeft: 4,
  color: 'gray', // To set ellipsis color
  $nest: {
    '& > *': {
      color: 'white',
    },
  },
});

const exclusionClass = style({
  color: 'gray', // To set ellipsis color
  $nest: {
    '& > *': {
      color: '#ff6161',
    },
  },
});

const loaderClass = style({
  position: 'absolute',
  bottom: 0,
  left: 0,
  width: '100%',
});
